import { User } from "redux/Slice/user-slice";
import { AUTH_DATA } from "Helpers/Roles/authData";

export type VisibilityRole =
  | "patient"
  | "guardian"
  | "doctor"
  | "sponsor"
  | "admin"
  | "editor";

const POLL_AWARE_ROLES: VisibilityRole[] = [
  "patient",
  "guardian",
  "doctor",
  "sponsor",
];

export interface VisibilityRule {
  role: VisibilityRole;
  shouldPoll?: boolean;
}

export interface VisibilityConfig {
  rules: VisibilityRule[];
  match: "all" | "any";
}

const getUserRoles = (user: User): string[] =>
  user?.roles?.map((r) => r?.role?.role) ?? [];

const userHasRole = (rule: VisibilityRule, userRoles: string[]): boolean => {
  if (rule.role === "admin") {
    return AUTH_DATA.ADMIN_ROLES.some((r) => userRoles.includes(r));
  }
  if (rule.role === "editor") {
    return AUTH_DATA.EDITOR.some((r) => userRoles.includes(r));
  }
  return userRoles.includes(rule.role);
};

const checkRule = (
  rule: VisibilityRule,
  user: User,
  hasFilledPoll: boolean
): boolean => {
  const userRoles = getUserRoles(user);
  if (!userHasRole(rule, userRoles)) return false;

  if (!rule.shouldPoll) return true;
  if (!POLL_AWARE_ROLES.includes(rule.role)) return true;

  return hasFilledPoll;
};

export const isVisible = (
  config: VisibilityConfig | undefined,
  user: User,
  hasFilledPoll: boolean
): boolean => {
  if (!config || !config.rules?.length) return true;
  return config.match === "all"
    ? config.rules.every((r) => checkRule(r, user, hasFilledPoll))
    : config.rules.some((r) => checkRule(r, user, hasFilledPoll));
};

export const matchesAnyUserRole = (
  config: VisibilityConfig | undefined,
  userRoles: string[]
): boolean => {
  if (!config || !config.rules?.length) return true;
  return config.rules.some((r) => userHasRole(r, userRoles));
};
